import Markdown from 'react-markdown';
import styled from 'styled-components';
import React from 'react';
import { Modal } from 'react-bootstrap';
import { useMeasure } from 'react-use';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../state/store';
import { IoIosCloseCircle } from 'react-icons/io';
import remarkGfm from 'remark-gfm';

interface AiCommentaryViewProps {
  show: boolean;
  closeModal: () => void;
  data: any;
}

const AiCommentaryView = ({ show, closeModal, data }: AiCommentaryViewProps) => {
  // Get modal container height and width
  const [modalContainerRef, modalContainerDims] = useMeasure();
  const theme = useSelector((state: RootState) => state.theme.mode);

  return (
    <Modal dialogClassName="custom curved-wide-report" show={show} size="lg">
      <ModalContent
        $isDark={theme !== 'light'}
        ref={modalContainerRef as unknown as React.RefObject<HTMLDivElement>}
      >
        <div className="header" style={{ height: '60px' }}>
          <h4 className="modal-title">AI Suggestions</h4>
          <span
            className={`modal-close-btn ${theme != 'light' ? 'dark' : ''}`}
            onClick={closeModal}
          >
            <IoIosCloseCircle fontSize="45px" fill={theme !== 'light' ? '#dfdcdc' : '#000'} />
          </span>
        </div>

        <div
          className="ai-result"
          style={{
            height: `${modalContainerDims.height - 110}px`,
            width: `${modalContainerDims.width - 50}px`
          }}
        >
          <Markdown remarkPlugins={[remarkGfm]}>{data?.result}</Markdown>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AiCommentaryView;

const ModalContent = styled.div<{ $isDark: boolean }>`
  height: 100%;
  width: 100%;
  .header {
    padding: 10px 25px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .modal-title {
      text-decoration: underline;
    }

    .modal-close-btn {
      cursor: pointer;
      position: absolute;
      top: -23px;
      right: -23px;
      background: #ffffff;
      width: auto;
      border-radius: 25px;
      &.dark {
        background: #333333;
      }
    }
  }
  .ai-result {
    transform: translate(25px, 25px);
    position: fixed;
    overflow: auto;
    font-size: 16px;
    line-height: 1.6;
    color: ${(props) => (props.$isDark ? '#ffffff' : '#333333')};
    position: relative;

    /* Headings */
    h1 {
      font-size: 24px;
      font-weight: 600;
      margin: 24px 0 16px 0;
      color: ${(props) => (props.$isDark ? '#ffffff' : '#1a1a1a')};
      border-bottom: 2px solid ${(props) => (props.$isDark ? '#444444' : '#e1e4e8')};
      padding-bottom: 8px;
    }

    h2 {
      font-size: 20px;
      font-weight: 600;
      margin: 20px 0 12px 0;
      color: ${(props) => (props.$isDark ? '#ffffff' : '#1a1a1a')};
    }

    h3 {
      font-size: 18px;
      font-weight: 500;
      margin: 16px 0 8px 0;
      color: ${(props) => (props.$isDark ? '#ffffff' : '#1a1a1a')};
    }

    h4,
    h5,
    h6 {
      font-size: 16px;
      font-weight: 500;
      margin: 12px 0 6px 0;
      color: ${(props) => (props.$isDark ? '#ffffff' : '#1a1a1a')};
    }

    /* Paragraphs */
    p {
      margin: 12px 0;
      font-size: 16px;
      line-height: 1.6;
    }

    /* Lists */
    ul,
    ol {
      margin: 12px 0;
      padding-left: 24px;

      li {
        margin: 6px 0;
        line-height: 1.5;
      }
    }

    /* Code blocks */
    pre {
      background: ${(props) => (props.$isDark ? '#1e1e1e' : '#f6f8fa')};
      border: 1px solid ${(props) => (props.$isDark ? '#444444' : '#e1e4e8')};
      border-radius: 6px;
      padding: 16px;
      margin: 16px 0;
      overflow-x: auto;
      font-size: 14px;

      code {
        background: transparent;
        padding: 0;
        border: none;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      }
    }

    /* Inline code */
    code {
      background: ${(props) => (props.$isDark ? '#333333' : '#f1f3f4')};
      color: ${(props) => (props.$isDark ? '#ff6b6b' : '#d73a49')};
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 14px;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    }

    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
      font-size: 14px;
      background: ${(props) => (props.$isDark ? '#1a1a1a' : '#ffffff')};
      border: 1px solid ${(props) => (props.$isDark ? '#444444' : '#e1e4e8')};
      border-radius: 6px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      thead {
        background: ${(props) => (props.$isDark ? '#2a2a2a' : '#f6f8fa')};

        tr {
          th {
            padding: 12px 16px;
            text-align: left;
            font-weight: 600;
            color: ${(props) => (props.$isDark ? '#ffffff' : '#24292e')};
            border-bottom: 2px solid ${(props) => (props.$isDark ? '#444444' : '#e1e4e8')};
            font-size: 14px;
          }
        }
      }

      tbody {
        tr {
          &:nth-child(even) {
            background: ${(props) => (props.$isDark ? '#222222' : '#f9f9f9')};
          }

          &:hover {
            background: ${(props) => (props.$isDark ? '#2a2a2a' : '#f0f0f0')};
          }

          td {
            padding: 12px 16px;
            border-bottom: 1px solid ${(props) => (props.$isDark ? '#333333' : '#e1e4e8')};
            color: ${(props) => (props.$isDark ? '#cccccc' : '#586069')};
            vertical-align: top;

            &:first-child {
              font-weight: 500;
              color: ${(props) => (props.$isDark ? '#ffffff' : '#24292e')};
            }
          }

          &:last-child td {
            border-bottom: none;
          }
        }
      }
    }

    /* Blockquotes */
    blockquote {
      border-left: 4px solid ${(props) => (props.$isDark ? '#555555' : '#dfe2e5')};
      padding: 0 16px;
      margin: 16px 0;
      color: ${(props) => (props.$isDark ? '#cccccc' : '#6a737d')};
      font-style: italic;

      p {
        margin: 8px 0;
      }
    }

    /* Links */
    a {
      color: ${(props) => (props.$isDark ? '#58a6ff' : '#0366d6')};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    /* Horizontal rules */
    hr {
      border: none;
      border-top: 1px solid ${(props) => (props.$isDark ? '#444444' : '#e1e4e8')};
      margin: 24px 0;
    }

    /* Strong and emphasis */
    strong {
      font-weight: 600;
      color: ${(props) => (props.$isDark ? '#ffffff' : '#24292e')};
    }

    em {
      font-style: italic;
    }
  }
`;
